import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import styled from "@emotion/styled";
import { useNetworkStore } from "../stores/network";
import { useAuthStore } from "../stores/auth";
import { useLabels, useAddLabel, useDeleteLabel } from "../api/hooks";
import { useMyTxs } from "../stores/my-txs";
import { useMyLabels } from "../stores/my-labels";

import {
  PageContainer, PageTitle, Card, Table, TableWrapper, Loading,
  Flex, Button, Input, Badge, SectionTitle,
} from "../components/ui";
import { HexDisplay } from "../components/HexDisplay";
import { theme } from "../lib/theme";


const SectionNote = styled.p`
  color: ${theme.colors.textMuted};
  font-size: ${theme.fontSize.xs};
  margin: 0 0 ${theme.spacing.sm};
`;

const AdminNotice = styled.div`
  border: 1px dashed ${theme.colors.border};
  border-radius: ${theme.radius.sm};
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  margin-bottom: ${theme.spacing.sm};
  font-size: ${theme.fontSize.xs};
  color: ${theme.colors.textMuted};
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: ${theme.spacing.md};
`;

const AdminLink = styled(Link)`
  color: ${theme.colors.primary};
  text-decoration: none;
  font-weight: 500;
  &:hover { text-decoration: underline; }
`;

export function Labels() {
  const { selectedNetwork } = useNetworkStore();
  const { isAdmin } = useAuthStore();
  const { data: labels, isLoading } = useLabels(selectedNetwork);
  const addLabel = useAddLabel(selectedNetwork);
  const deleteLabel = useDeleteLabel(selectedNetwork);
  const { txs, add: addTx, remove: removeTx } = useMyTxs();
  // Select the stable map reference; derive the per-network list with useMemo
  // to avoid returning a fresh `[]` from the selector each render (zustand
  // would treat that as a state change and trigger an infinite loop).
  const labelsMap = useMyLabels((s) => s.labels);
  const myLabels = useMemo(
    () => labelsMap[selectedNetwork] ?? [],
    [labelsMap, selectedNetwork],
  );
  const addMyLabel = useMyLabels((s) => s.add);
  const removeMyLabel = useMyLabels((s) => s.remove);

  // Shared label form
  const [address, setAddress] = useState("");
  const [label, setLabel] = useState("");
  const [contractType, setContractType] = useState("");

  // Private label form
  const [myAddress, setMyAddress] = useState("");
  const [myLabel, setMyLabel] = useState("");
  const [myContractType, setMyContractType] = useState("");

  // My-Tx form
  const [txHash, setTxHash] = useState("");
  const [txLabel, setTxLabel] = useState("");

  const handleAddLabel = (e: React.FormEvent) => {
    e.preventDefault();
    if (!address || !label) return;
    addLabel.mutate(
      { address, label, contractType: contractType || undefined },
      { onSuccess: () => { setAddress(""); setLabel(""); setContractType(""); } },
    );
  };

  const handleAddMyLabel = (e: React.FormEvent) => {
    e.preventDefault();
    if (!myAddress || !myLabel) return;
    addMyLabel(selectedNetwork, myAddress, myLabel, myContractType || undefined);
    setMyAddress(""); setMyLabel(""); setMyContractType("");
  };

  const handleAddTx = (e: React.FormEvent) => {
    e.preventDefault();
    if (!txHash) return;
    addTx(txHash, txLabel || undefined);
    setTxHash("");
    setTxLabel("");
  };

  // Set of addresses you've shadowed with a private label — used to mark
  // shared rows that you've personally renamed.
  const privateAddrSet = new Set(myLabels.map((l) => l.address));

  if (isLoading) return <Loading />;

  return (
    <PageContainer>
      <PageTitle>Labels</PageTitle>

      {/* ── Shared Contract Labels ── */}
      <SectionTitle>Shared Contract Labels</SectionTitle>
      <SectionNote>
        Visible to everyone on this network. Admin only — sign in to add or remove.
      </SectionNote>

      {isAdmin() ? (
        <Card style={{ marginBottom: theme.spacing.sm }}>
          <form onSubmit={handleAddLabel}>
            <Flex gap="12px">
              <Input placeholder="Contract address (0x...)" value={address} onChange={(e) => setAddress(e.target.value)} style={{ flex: 2 }} />
              <Input placeholder="Label" value={label} onChange={(e) => setLabel(e.target.value)} style={{ flex: 1 }} />
              <Input placeholder="Type (Token, AMM...)" value={contractType} onChange={(e) => setContractType(e.target.value)} style={{ flex: 1 }} />
              <Button type="submit" disabled={!address || !label}>Add</Button>
            </Flex>
          </form>
        </Card>
      ) : (
        <AdminNotice>
          <span>Adding a shared label requires admin sign-in.</span>
          <AdminLink to={`/${selectedNetwork}/admin`}>Go to admin →</AdminLink>
        </AdminNotice>
      )}

      <Card style={{ padding: 0, overflow: "hidden", marginBottom: theme.spacing.lg }}>
        <TableWrapper>
          <Table>
            <thead>
              <tr>
                <th>Address</th>
                <th>Label</th>
                <th>Type</th>
                {isAdmin() && <th></th>}
              </tr>
            </thead>
            <tbody>
              {labels?.map((l) => {
                const overridden = privateAddrSet.has(l.address.toLowerCase());
                return (
                  <tr key={l.id}>
                    <td><HexDisplay address={l.address} /></td>
                    <td>
                      {l.label}
                      {overridden && (
                        <span style={{ marginLeft: 8, color: theme.colors.textMuted, fontSize: 10 }}>
                          (overridden by your private label)
                        </span>
                      )}
                    </td>
                    <td>{l.contractType ? <Badge>{l.contractType}</Badge> : "—"}</td>
                    {isAdmin() && (
                      <td>
                        <Button variant="danger" onClick={() => deleteLabel.mutate(l.id)} style={{ padding: "2px 8px", fontSize: "11px" }}>
                          Remove
                        </Button>
                      </td>
                    )}
                  </tr>
                );
              })}
              {!labels?.length && (
                <tr><td colSpan={isAdmin() ? 4 : 3} style={{ color: theme.colors.textMuted, textAlign: "center" }}>No labels yet</td></tr>
              )}
            </tbody>
          </Table>
        </TableWrapper>
      </Card>

      {/* ── My Contract Labels (local) ── */}
      <SectionTitle>My Contract Labels</SectionTitle>
      <SectionNote>
        Local only — stored in your browser, applies to {selectedNetwork} only.
        Overrides shared labels for you.
      </SectionNote>

      <Card style={{ marginBottom: theme.spacing.sm }}>
        <form onSubmit={handleAddMyLabel}>
          <Flex gap="12px">
            <Input placeholder="Contract address (0x...)" value={myAddress} onChange={(e) => setMyAddress(e.target.value)} style={{ flex: 2 }} />
            <Input placeholder="Label" value={myLabel} onChange={(e) => setMyLabel(e.target.value)} style={{ flex: 1 }} />
            <Input placeholder="Type (Token, AMM...)" value={myContractType} onChange={(e) => setMyContractType(e.target.value)} style={{ flex: 1 }} />
            <Button type="submit" disabled={!myAddress || !myLabel}>Add</Button>
          </Flex>
        </form>
      </Card>

      <Card style={{ padding: 0, overflow: "hidden", marginBottom: theme.spacing.lg }}>
        <TableWrapper>
          <Table>
            <thead>
              <tr>
                <th>Address</th>
                <th>Label</th>
                <th>Type</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {myLabels.map((l) => (
                <tr key={l.address}>
                  <td><HexDisplay address={l.address} mode="hex" /></td>
                  <td><Badge color={theme.colors.warning}>{l.label}</Badge></td>
                  <td>{l.contractType ? <Badge>{l.contractType}</Badge> : "—"}</td>
                  <td>
                    <Button variant="danger" onClick={() => removeMyLabel(selectedNetwork, l.address)} style={{ padding: "2px 8px", fontSize: "11px" }}>
                      Remove
                    </Button>
                  </td>
                </tr>
              ))}
              {myLabels.length === 0 && (
                <tr><td colSpan={4} style={{ color: theme.colors.textMuted, textAlign: "center" }}>No private labels yet</td></tr>
              )}
            </tbody>
          </Table>
        </TableWrapper>
      </Card>

      {/* ── My Transactions (local) ── */}
      <SectionTitle>My Transactions</SectionTitle>
      <SectionNote>
        Local only — stored in your browser, never sent to the server.
      </SectionNote>

      <Card style={{ marginBottom: theme.spacing.sm }}>
        <form onSubmit={handleAddTx}>
          <Flex gap="12px">
            <Input
              placeholder="Transaction hash (0x...)"
              value={txHash}
              onChange={(e) => setTxHash(e.target.value)}
              style={{ flex: 2 }}
            />
            <Input
              placeholder="Label (optional)"
              value={txLabel}
              onChange={(e) => setTxLabel(e.target.value)}
              style={{ flex: 1 }}
            />
            <Button type="submit" disabled={!txHash}>Track</Button>
          </Flex>
        </form>
      </Card>

      <Card style={{ padding: 0, overflow: "hidden" }}>
        <TableWrapper>
          <Table>
            <thead>
              <tr>
                <th>Hash</th>
                <th>Label</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {txs.map((t) => (
                <tr key={t.hash}>
                  <td>
                    <Flex gap="4px" align="center">
                      <Link to={`/${selectedNetwork}/tx/${t.hash}`} style={{ color: theme.colors.primary, textDecoration: "none" }}>
                        <HexDisplay address={t.hash} link={false} />
                      </Link>
                    </Flex>
                  </td>
                  <td>{t.label ? <Badge color={theme.colors.warning}>{t.label}</Badge> : "—"}</td>
                  <td>
                    <Button variant="danger" onClick={() => removeTx(t.hash)} style={{ padding: "2px 8px", fontSize: "11px" }}>
                      Remove
                    </Button>
                  </td>
                </tr>
              ))}
              {txs.length === 0 && (
                <tr><td colSpan={3} style={{ color: theme.colors.textMuted, textAlign: "center" }}>
                  No tracked transactions. Add a tx hash above, or click "Track as Mine" on any tx detail page.
                </td></tr>
              )}
            </tbody>
          </Table>
        </TableWrapper>
      </Card>
    </PageContainer>
  );
}
